import { EVENT_STATUS } from '@hedepy/common/enums/EVENT'
import type { Event } from '@hedepy/common/schemas/modules/event/event'
import dayjs from 'dayjs'
import { type KeyedMutator } from 'swr'

import { sendPurchaseFbEvent } from '@/legacy-src/analytics/fbPixelData'
import getCustomerType from '@/legacy-src/utilities/isB2C'

import getGACookie from '../../analytics/getGaCookie'
import ClientActions, { SHOW_THERAPISTS } from '../../redux/ClientRedux'
import GeneralActions from '../../redux/GeneralRedux'
import { type ApiEndpoints } from '../../services/api'
import { dateToMonthYear } from '../../utilities/date'
import notifyMobileApp, { NotifyAppMessages } from '../../utilities/notifyMobileApp'
import toaster from '../../utilities/toaster'
import { PriorityPaymentMethodType, type HandleReserveEventOptions } from './interface'
import { type UsedEventsMap } from './useEventActions'

export const createUsedEventsMap = (events: Pick<Event, 'userSubscriptionId' | 'status' | 'startDate'>[]) => {
  const map: UsedEventsMap = {}
  const cutOffDate = dayjs().subtract(1, 'month').endOf('month')

  events.forEach((event) => {
    if (
      event.userSubscriptionId &&
      [EVENT_STATUS.RESERVED, EVENT_STATUS.COMPLETED, EVENT_STATUS.CANCELED_BY_CLIENT].includes(event.status) &&
      dayjs(event.startDate).isAfter(cutOffDate)
    ) {
      const monthYear = dateToMonthYear(event.startDate)
      const subscriptionId = event.userSubscriptionId

      const currentEventsForMonthYear = (map[subscriptionId] || {})[monthYear] || 0

      map[subscriptionId] = { ...(map[subscriptionId] || {}), [monthYear]: currentEventsForMonthYear + 1 }
    }
  })

  return map
}

export const _handleReserveEvent = async (
  {
    companyEventQuotaId,
    userSubscriptionId,
    paymentMethod = '',
    voucherCode,
    therapist,
    slot,
    isCounselling,
    membershipId,
  }: HandleReserveEventOptions,
  api: ApiEndpoints,
  clientId,
  dispatch,
  router,
  userIp,
  refetchEvents: KeyedMutator<Event[]>,
  refetchQuota: () => void,
  user,
) => {
  const purchaseIp = userIp
  const purchaseCookie = await getGACookie()
  const timezone = dayjs.tz.guess()

  let origin
  let originParam
  if (router.query?.rt) {
    origin = 'recommendedTherapist'
    originParam = +router.query.rt
  }

  if (slot.status === EVENT_STATUS.PRE_BOOKING) {
    origin = 'prebooking'
  }

  const { data } = await api.reserveEvents({
    events: [
      {
        startDate: slot.startDate,
        endDate: slot.endDate,
        serviceId: slot.serviceId,
        therapistUserId: therapist.therapistUserId,
        clientId,
        voucherCode,
        userSubscriptionId,
        companyEventQuotaId,
        id: slot.id,
        purchaseCookie,
        purchaseIp,
        counsellingSessionId: slot.counsellingSessionId,
        status: slot.status,
        membershipId,
      } as any,
    ],
    timezone,
    initialVoucherCall: !paymentMethod,
    paymentMethod,
    origin,
    originParam,
  })
  const returnedEvent = data[0]
  const { discountedPrice } = returnedEvent

  if (discountedPrice) {
    return { discountedPrice: returnedEvent.discountedPrice }
  }

  const isB2C = getCustomerType(returnedEvent).isB2C
  const isFirstBoughtEvent = returnedEvent.isFirstBoughtEvent
  const isPaidByVoucher = !!returnedEvent.voucherId
  const isPaidBySubscription = !!returnedEvent.userSubscriptionId
  const isPaidByFreeSession = returnedEvent.price && parseInt(returnedEvent.price, 10) === 0
  const isPaidByBenefits = Boolean(returnedEvent.companyEventQuotaId)
  const isAlreadyPaid = isPaidByVoucher || isPaidBySubscription || isPaidByFreeSession || isPaidByBenefits

  if (isAlreadyPaid) {
    notifyMobileApp(NotifyAppMessages.PAYMENT_SUCCESS)
    sendPurchaseFbEvent({ event: returnedEvent, user: { ...user, userIp } })
    if (isB2C && isFirstBoughtEvent) {
      sendPurchaseFbEvent({
        event: returnedEvent,
        user: { ...user, userIp },
        isAcquisition: true,
      })
    }
    refetchEvents()
  }

  if (isPaidByVoucher) {
    dispatch(GeneralActions.addGtmSuccessVoucherEvent([returnedEvent], therapist))
    dispatch(ClientActions.onChangeClientValue('reservationVoucher', {}))
    toaster.success('component.toaster.reservationByVoucherSuccess')
  } else if (isPaidByBenefits) {
    toaster.success('component.toaster.reservationByBenefitsSuccess')
    dispatch(
      GeneralActions.addGtmSuccessPaymentEvent(
        returnedEvent.id,
        { ...returnedEvent, therapist },
        `BENEFITS-QUOTA-${returnedEvent.companyEventQuotaId}-USER-${clientId}`,
        {
          ...user,
          userId: clientId,
        },
      ),
      refetchQuota(),
    )
  } else if (isPaidBySubscription) {
    toaster.success('component.toaster.reservationBySubscriptionSuccess')
    dispatch(
      GeneralActions.addGtmSuccessPaymentEvent(
        returnedEvent.id,
        { ...returnedEvent, therapist },
        `SUBSCRIPTION-${returnedEvent.userSubscriptionId}-USER-${clientId}`,
        {
          ...user,
          userId: clientId,
        },
      ),
    )
  } else if (isPaidByFreeSession) {
    toaster.success('component.toaster.appointmentReservation')
    dispatch(
      GeneralActions.addGtmSuccessPaymentEvent(
        returnedEvent.id,
        { ...returnedEvent, therapist },
        `FREE-SESSION-USER-${clientId}`,
        {
          ...user,
          userId: clientId,
        },
      ),
    )
  } else {
    dispatch(
      ClientActions.onMergeClientValue({
        showTherapists: SHOW_THERAPISTS.ABSOLVED_THERAPIST,
      }),
    )
    const response = await api.transaction(
      [returnedEvent].map((item) => item.id),
      paymentMethod,
    )

    const intentClientSecret = response.data.intent_client_secret
    const redirectUrl = response.data.redirect

    if (redirectUrl) {
      window.location.href = response.data.redirect
    } else if (intentClientSecret) {
      const notifyOptions = { intentClientSecret, amount: returnedEvent.price, label: 'Hedepy' }
      if (paymentMethod === PriorityPaymentMethodType.APPLE_PAY) {
        notifyMobileApp(NotifyAppMessages.PAYMENT_APPLE, notifyOptions)
      } else if (paymentMethod === PriorityPaymentMethodType.GOOGLE_PAY) {
        notifyMobileApp(NotifyAppMessages.PAYMENT_GOOGLE, notifyOptions)
      }
    }
  }

  if (isAlreadyPaid) {
    if (isCounselling) {
      router.push('/counselling?booked=true')
    } else if (returnedEvent.isCollision) {
      router.push('/client/reservation')
    } else if (!isPaidBySubscription && !isPaidByBenefits) {
      router.push('/client/calendar?afterPurchase=true')
    } else {
      router.push('/client/calendar')
    }
  }
}
